import React, { useState, useEffect } from 'react';
import { FileText, Save, AlertCircle, CheckCircle, Clock, History } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface LegalDocument {
  id: string;
  title: string;
  content: string;
  type: 'terms' | 'privacy';
  version: string;
  published_at: string;
  is_active: boolean;
  created_at: string;
}

interface DocumentVersion {
  version: string;
  published_at: string;
}

export default function LegalDocumentsManager() {
  const [termsDocument, setTermsDocument] = useState<LegalDocument | null>(null);
  const [privacyDocument, setPrivacyDocument] = useState<LegalDocument | null>(null);
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  const [termsVersions, setTermsVersions] = useState<DocumentVersion[]>([]);
  const [privacyVersions, setPrivacyVersions] = useState<DocumentVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState<'terms' | 'privacy'>('terms');

  useEffect(() => {
    fetchDocuments();
  }, []);

  async function fetchDocuments() {
    try {
      setLoading(true);
      setError('');
      
      // Fetch terms of use
      const { data: termsData, error: termsError } = await supabase
        .from('legal_documents')
        .select('*')
        .eq('type', 'terms')
        .eq('is_active', true)
        .single();
      
      if (termsError && termsError.code !== 'PGRST116') {
        throw termsError;
      }
      
      // Fetch privacy policy
      const { data: privacyData, error: privacyError } = await supabase
        .from('legal_documents')
        .select('*')
        .eq('type', 'privacy')
        .eq('is_active', true)
        .single();
      
      if (privacyError && privacyError.code !== 'PGRST116') {
        throw privacyError;
      }
      
      // Fetch document versions
      const { data: termsVersionsData } = await supabase
        .from('legal_documents')
        .select('version, published_at')
        .eq('type', 'terms')
        .order('published_at', { ascending: false });
      
      const { data: privacyVersionsData } = await supabase
        .from('legal_documents')
        .select('version, published_at')
        .eq('type', 'privacy')
        .order('published_at', { ascending: false });
      
      setTermsDocument(termsData);
      setTermsContent(termsData?.content || getDefaultTermsContent());
      setTermsVersions(termsVersionsData || []);
      
      setPrivacyDocument(privacyData);
      setPrivacyContent(privacyData?.content || getDefaultPrivacyContent());
      setPrivacyVersions(privacyVersionsData || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar documentos legais');
    } finally {
      setLoading(false);
    }
  }

  async function saveDocument(type: 'terms' | 'privacy') {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const content = type === 'terms' ? termsContent : privacyContent;
      const currentDoc = type === 'terms' ? termsDocument : privacyDocument;
      
      // Generate new version number
      const newVersion = currentDoc 
        ? incrementVersion(currentDoc.version) 
        : '1.0.0';
      
      // If there's an existing document, deactivate it
      if (currentDoc) {
        await supabase
          .from('legal_documents')
          .update({ is_active: false })
          .eq('id', currentDoc.id);
      }
      
      // Insert new document version
      const { error } = await supabase
        .from('legal_documents')
        .insert({
          title: type === 'terms' ? 'Termos de Uso' : 'Política de Privacidade',
          content,
          type,
          version: newVersion,
          is_active: true
        });
      
      if (error) throw error;
      
      setSuccess(`${type === 'terms' ? 'Termos de Uso' : 'Política de Privacidade'} atualizados com sucesso (versão ${newVersion})`);
      fetchDocuments();
    } catch (err: any) {
      setError(err.message || `Erro ao salvar ${type === 'terms' ? 'Termos de Uso' : 'Política de Privacidade'}`);
    } finally {
      setSaving(false);
    }
  }

  function incrementVersion(version: string): string {
    const parts = version.split('.').map(Number);
    parts[2] += 1;
    
    // Handle carrying over
    if (parts[2] >= 10) {
      parts[2] = 0;
      parts[1] += 1;
      
      if (parts[1] >= 10) {
        parts[1] = 0;
        parts[0] += 1;
      }
    }
    
    return parts.join('.');
  }

  function getDefaultTermsContent(): string {
    return `<h2>Termos de Uso</h2>
<p>Bem-vindo aos Termos de Uso do aplicativo SdT BSB. Ao acessar ou usar nosso aplicativo, você concorda em cumprir e estar vinculado a estes termos.</p>

<h3>1. Aceitação dos Termos</h3>
<p>Ao acessar e usar o aplicativo SdT BSB, você concorda em cumprir e estar vinculado a estes Termos de Uso. Se você não concordar com qualquer parte destes termos, não poderá acessar o aplicativo.</p>

<h3>2. Uso do Aplicativo</h3>
<p>O aplicativo SdT BSB é fornecido para uso pessoal e não comercial. Você concorda em usar o aplicativo apenas para fins legais e de acordo com estes Termos de Uso.</p>

<h3>3. Contas de Usuário</h3>
<p>Para acessar determinados recursos do aplicativo, você precisará criar uma conta. Você é responsável por manter a confidencialidade de suas credenciais de conta e por todas as atividades que ocorrerem em sua conta.</p>

<h3>4. Conteúdo do Usuário</h3>
<p>Ao enviar conteúdo para o aplicativo (como pedidos de oração, testemunhos, etc.), você concede à Igreja Sal da Terra BSB uma licença mundial, não exclusiva, isenta de royalties para usar, reproduzir e distribuir esse conteúdo em relação ao aplicativo.</p>

<h3>5. Conduta do Usuário</h3>
<p>Você concorda em não usar o aplicativo para:</p>
<ul>
  <li>Violar qualquer lei aplicável</li>
  <li>Publicar conteúdo difamatório, obsceno ou ofensivo</li>
  <li>Assediar, ameaçar ou intimidar outros usuários</li>
  <li>Tentar acessar áreas restritas do aplicativo</li>
  <li>Interferir no funcionamento do aplicativo</li>
</ul>

<h3>6. Propriedade Intelectual</h3>
<p>O aplicativo e todo o seu conteúdo, recursos e funcionalidades são de propriedade da Igreja Sal da Terra BSB e estão protegidos por leis de direitos autorais, marcas registradas e outras leis de propriedade intelectual.</p>

<h3>7. Limitação de Responsabilidade</h3>
<p>O aplicativo é fornecido "como está" e "conforme disponível", sem garantias de qualquer tipo. A Igreja Sal da Terra BSB não será responsável por quaisquer danos diretos, indiretos, incidentais, consequenciais ou punitivos resultantes do uso ou incapacidade de usar o aplicativo.</p>

<h3>8. Modificações dos Termos</h3>
<p>A Igreja Sal da Terra BSB reserva-se o direito de modificar estes Termos de Uso a qualquer momento. As alterações entrarão em vigor imediatamente após a publicação dos termos atualizados no aplicativo.</p>

<h3>9. Rescisão</h3>
<p>A Igreja Sal da Terra BSB pode encerrar ou suspender seu acesso ao aplicativo a qualquer momento, por qualquer motivo, sem aviso prévio.</p>

<h3>10. Lei Aplicável</h3>
<p>Estes Termos de Uso são regidos pelas leis do Brasil. Qualquer disputa relacionada a estes termos será submetida à jurisdição exclusiva dos tribunais de Brasília, DF.</p>`;
  }

  function getDefaultPrivacyContent(): string {
    return `<h2>Política de Privacidade</h2>
<p>A Igreja Sal da Terra BSB está comprometida em proteger sua privacidade. Esta Política de Privacidade explica como coletamos, usamos e protegemos suas informações pessoais.</p>

<h3>1. Informações que Coletamos</h3>
<p>Podemos coletar os seguintes tipos de informações:</p>
<ul>
  <li>Informações de identificação pessoal (nome, email, telefone)</li>
  <li>Informações de login e autenticação</li>
  <li>Conteúdo gerado pelo usuário (pedidos de oração, testemunhos, etc.)</li>
  <li>Informações de uso e interação com o aplicativo</li>
  <li>Informações do dispositivo e conexão</li>
</ul>

<h3>2. Como Usamos Suas Informações</h3>
<p>Usamos suas informações para:</p>
<ul>
  <li>Fornecer e manter o aplicativo</li>
  <li>Personalizar sua experiência</li>
  <li>Processar pedidos de oração e solicitações de orientação espiritual</li>
  <li>Enviar notificações importantes sobre o aplicativo ou eventos da igreja</li>
  <li>Melhorar nosso aplicativo e serviços</li>
  <li>Cumprir obrigações legais</li>
</ul>

<h3>3. Compartilhamento de Informações</h3>
<p>Não vendemos, alugamos ou compartilhamos suas informações pessoais com terceiros para fins comerciais. Podemos compartilhar suas informações:</p>
<ul>
  <li>Com líderes da igreja para responder a pedidos de oração ou orientação espiritual</li>
  <li>Com provedores de serviços que nos ajudam a operar o aplicativo</li>
  <li>Quando exigido por lei ou para proteger nossos direitos</li>
</ul>

<h3>4. Segurança de Dados</h3>
<p>Implementamos medidas de segurança técnicas e organizacionais para proteger suas informações pessoais contra acesso não autorizado, perda ou alteração.</p>

<h3>5. Seus Direitos</h3>
<p>Você tem o direito de:</p>
<ul>
  <li>Acessar suas informações pessoais</li>
  <li>Corrigir informações imprecisas</li>
  <li>Excluir suas informações pessoais</li>
  <li>Retirar seu consentimento a qualquer momento</li>
  <li>Opor-se ao processamento de suas informações</li>
</ul>

<h3>6. Cookies e Tecnologias Semelhantes</h3>
<p>Usamos cookies e tecnologias semelhantes para melhorar sua experiência, entender como o aplicativo é usado e personalizar o conteúdo.</p>

<h3>7. Alterações a Esta Política</h3>
<p>Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos você sobre quaisquer alterações significativas publicando a nova Política de Privacidade no aplicativo.</p>

<h3>8. Contato</h3>
<p>Se você tiver dúvidas sobre esta Política de Privacidade, entre em contato conosco pelo email: privacidade@sdtbsb.com.br</p>`;
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="h-6 w-6 text-brand-teal" />
        <h2 className="text-xl font-semibold text-brand-teal">Documentos Legais</h2>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 text-green-600 rounded-lg flex items-start">
          <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('terms')}
              className={`py-4 px-6 font-medium text-sm border-b-2 ${
                activeTab === 'terms'
                  ? 'border-brand-teal text-brand-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Termos de Uso
            </button>
            <button
              onClick={() => setActiveTab('privacy')}
              className={`py-4 px-6 font-medium text-sm border-b-2 ${
                activeTab === 'privacy'
                  ? 'border-brand-teal text-brand-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Política de Privacidade
            </button>
          </nav>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Editor */}
        <div className="lg:col-span-3">
          {activeTab === 'terms' ? (
            <div>
              <div className="mb-4 flex justify-between items-center">
                <div>
                  <h3 className="font-medium text-gray-900">Editor de Termos de Uso</h3>
                  {termsDocument && (
                    <p className="text-sm text-gray-500">
                      Versão atual: {termsDocument.version} (publicada em {formatDate(termsDocument.published_at)})
                    </p>
                  )}
                </div>
                <button
                  onClick={() => saveDocument('terms')}
                  disabled={saving}
                  className="bg-brand-teal text-white px-4 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Salvar e Publicar</span>
                    </>
                  )}
                </button>
              </div>
              <textarea
                value={termsContent}
                onChange={(e) => setTermsContent(e.target.value)}
                className="w-full h-[500px] p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal font-mono text-sm"
              />
              <p className="mt-2 text-xs text-gray-500">
                Você pode usar tags HTML para formatação (h1, h2, h3, p, ul, li, strong, em, etc).
              </p>
            </div>
          ) : (
            <div>
              <div className="mb-4 flex justify-between items-center">
                <div>
                  <h3 className="font-medium text-gray-900">Editor de Política de Privacidade</h3>
                  {privacyDocument && (
                    <p className="text-sm text-gray-500">
                      Versão atual: {privacyDocument.version} (publicada em {formatDate(privacyDocument.published_at)})
                    </p>
                  )}
                </div>
                <button
                  onClick={() => saveDocument('privacy')}
                  disabled={saving}
                  className="bg-brand-teal text-white px-4 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Salvar e Publicar</span>
                    </>
                  )}
                </button>
              </div>
              <textarea
                value={privacyContent}
                onChange={(e) => setPrivacyContent(e.target.value)}
                className="w-full h-[500px] p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal font-mono text-sm"
              />
              <p className="mt-2 text-xs text-gray-500">
                Você pode usar tags HTML para formatação (h1, h2, h3, p, ul, li, strong, em, etc).
              </p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <History className="h-4 w-4 text-brand-teal" />
              <span>Histórico de Versões</span>
            </h3>
            
            {activeTab === 'terms' ? (
              <div className="space-y-3">
                {termsVersions.length > 0 ? (
                  termsVersions.map((version, index) => (
                    <div key={index} className="p-3 bg-white rounded-lg shadow-sm">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-brand-teal">v{version.version}</span>
                        {index === 0 && (
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                            Atual
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatDate(version.published_at)}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">Nenhuma versão publicada</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {privacyVersions.length > 0 ? (
                  privacyVersions.map((version, index) => (
                    <div key={index} className="p-3 bg-white rounded-lg shadow-sm">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-brand-teal">v{version.version}</span>
                        {index === 0 && (
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                            Atual
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatDate(version.published_at)}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">Nenhuma versão publicada</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}